import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { decimalInput, integerInput } from '../domain/numbers.ts';
import { lowercasesType, uppercasesType } from '../domain/text.ts';
import { EyeIcon, EyeOffIcon } from './icons.tsx';
import { convertChange, lowercaseChange, uppercaseChange } from './uppercase.ts';

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string | undefined;
  hint?: ReactNode;
  required?: boolean;
  /**
   * Campo numérico: 'integer' acepta solo enteros de 1 en adelante (cantidades, días); 'decimal', solo números
   * con hasta 2 decimales (montos, porcentajes). Lo demás no se escribe.
   */
  numeric?: 'integer' | 'decimal';
}

// Todo texto escrito se convierte a mayúsculas al escribir; el correo, a minúsculas, y la contraseña queda
// tal cual (regla en AGENTS.md). Una contraseña trae un botón para ver lo que se escribe; al verla, el
// texto sigue sin convertirse porque la regla se decide con el tipo declarado.
export function Field({ label, error, hint, required, numeric, onChange, ...input }: FieldProps) {
  const id = useId();
  const [revealed, setRevealed] = useState(false);
  const isPassword = input.type === 'password';
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const uppercase = !numeric && uppercasesType(input.type);
  const lowercase = lowercasesType(input.type);
  const numericChange = numeric
    ? convertChange(numeric === 'integer' ? integerInput : decimalInput, onChange)
    : undefined;
  const control = (
    <input
      autoCapitalize={uppercase ? 'characters' : lowercase || isPassword || numeric ? 'none' : undefined}
      {...(numeric ? { inputMode: numeric === 'integer' ? 'numeric' : 'decimal', autoComplete: 'off' } : {})}
      {...input}
      {...(isPassword ? { type: revealed ? 'text' : 'password', autoCorrect: 'off', spellCheck: false } : {})}
      onChange={
        numericChange ?? (uppercase ? uppercaseChange(onChange) : lowercase ? lowercaseChange(onChange) : onChange)
      }
      id={id}
      required={required}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy}
    />
  );
  return (
    <div className={`field${error ? ' field-invalid' : ''}`}>
      <label htmlFor={id}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      {isPassword ? (
        <div className="field-password">
          {control}
          <button
            type="button"
            className="field-reveal"
            aria-label="Mostrar contraseña"
            aria-pressed={revealed}
            aria-controls={id}
            title={revealed ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            onClick={() => setRevealed((value) => !value)}
          >
            {revealed ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>
      ) : (
        control
      )}
      {hint ? (
        <p id={hintId} className="field-hint">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="field-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
